var __values = (this && this.__values) || function (o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
};
import { Elections } from '../../../../lib/graph/elections';
import { merge, range } from 'lodash';
function formatElectionResult(property, id, result, dissenterIds) {
    var _a;
    var dissenters = dissenterIds.reduce(function (acc, dissenter) {
        var _a;
        acc[dissenter] = (_a = {}, _a[property] = !result, _a);
        return acc;
    }, {});
    dissenters[id] = (_a = {}, _a[property] = result, _a);
    return dissenters;
}
function accumulateElectionResults(electionResults) {
    // a formatted election result is a BdGraphStateMap
    // this makes it easier to merge them with by node id instead of by property.
    return electionResults.reduce(function (acc, er) {
        var properties = Object.keys(er);
        var formattedElectionResults = properties.map(function (property) {
            return formatElectionResult(property, er[property].id, er[property].result, er[property].dissenters);
        });
        var merged = formattedElectionResults.reduce(function (_acc, result) {
            return merge(_acc, result);
        }, {});
        Object.keys(merged).forEach(function (nodeId) {
            acc[nodeId] = merged[nodeId];
        });
        return acc;
    }, {});
}
// In our pursuit of minimum synchronization, we must traverse the graph
// one last time and prune successor nodes that are the same as their predecessor,
// and thus redundant.
function pruneSyncGraph(syncGraph, successorIds, properties, graph) {
    var e_1, _a;
    // collect props to prune
    var propsToPrune = successorIds.reduce(function (acc, successorId) {
        var e_2, _a;
        var predecessorId = graph.predecessors[successorId];
        var successor = syncGraph[successorId];
        var predecessor = syncGraph[predecessorId];
        try {
            for (var properties_1 = __values(properties), properties_1_1 = properties_1.next(); !properties_1_1.done; properties_1_1 = properties_1.next()) {
                var prop = properties_1_1.value;
                if (successor[prop] === predecessor[prop]) {
                    acc.push({ successorId: successorId, prop: prop });
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (properties_1_1 && !properties_1_1.done && (_a = properties_1.return)) _a.call(properties_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        return acc;
    }, []);
    try {
        // prune props, and the whole node if empty.
        for (var propsToPrune_1 = __values(propsToPrune), propsToPrune_1_1 = propsToPrune_1.next(); !propsToPrune_1_1.done; propsToPrune_1_1 = propsToPrune_1.next()) {
            var prop = propsToPrune_1_1.value;
            delete syncGraph[prop.successorId][prop.prop];
            if (Object.keys(syncGraph[prop.successorId]).length === 0) {
                delete syncGraph[prop.successorId];
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (propsToPrune_1_1 && !propsToPrune_1_1.done && (_a = propsToPrune_1.return)) _a.call(propsToPrune_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return syncGraph;
}
// calculate inheritable graph state
export function calculateMiniminumSynchronization(graph, syncProperties, leafStateMap) {
    var properties = Object.keys(syncProperties);
    var initialVoterState = Elections.getInitialVoterState(leafStateMap, properties);
    var initialDepth = Elections.getInitialElectionDepth(leafStateMap, graph);
    var syncGraph = range(initialDepth, -1).reduce(function (acc, depth) {
        var elections = Elections.getEligibleElections(graph, Object.keys(acc.remainingVoters), depth);
        var electionResults = Elections.getElectionResults(graph, acc.remainingVoters, elections, syncProperties);
        var accumResults = accumulateElectionResults(electionResults.results);
        Object.keys(accumResults).forEach(function (key) {
            acc.syncGraph[key] = accumResults[key];
        });
        acc.remainingVoters = electionResults.remainingVoters;
        return acc;
    }, { syncGraph: {}, remainingVoters: initialVoterState }).syncGraph;
    // At this point, syncGraph has all the correct property values for all nodes,
    // but it's not the minimum set.
    // see pruneSyncGraph
    var successorIds = Object.keys(syncGraph).filter(function (id) {
        // is this id a root -> filter out
        if (graph.roots[id]) {
            return false;
        }
        // is this a successor? -> include
        if (graph.successors[id]) {
            return true;
        }
        return false;
    });
    var minSync = pruneSyncGraph(syncGraph, successorIds, properties, graph);
    return minSync;
}
